/**
 * Cache Management Type Definitions
 *
 * Defines all types related to cache management for the Admin Dashboard.
 * These types mirror the backend cache management schemas.
 */

/**
 * All available cache namespaces — the single source of truth.
 * `CacheNamespace` is derived from this list, and the API response schemas
 * (src/lib/api.ts) build their namespace enums from it, so adding a namespace
 * here is the only edit required to keep the dashboard in sync with the backend.
 */
export const CACHE_NAMESPACES = [
  'menu',
  'modifier-display',
  'modifier-global',
  'vehicle-reference',
  'eighty-sixed',
  'restaurant',
  'calendar',
  'product-options',
  'audit-aggregate',
] as const;

/**
 * Cache Namespace Type
 * Identifies different cache categories in the system
 */
export type CacheNamespace = (typeof CACHE_NAMESPACES)[number];

/**
 * Cache Namespace Display Names
 * Human-readable labels for cache namespaces
 */
export const CACHE_NAMESPACE_LABELS: Record<CacheNamespace, string> = {
  menu: 'Menu Cache',
  'modifier-display': 'Restaurant Modifiers',
  'modifier-global': 'Global Modifiers',
  'vehicle-reference': 'Vehicle Reference',
  'eighty-sixed': "86'd Items",
  restaurant: 'Restaurant Details',
  calendar: 'Restaurant Calendars',
  'product-options': 'Product Options',
  'audit-aggregate': 'Audit Aggregates',
};

/**
 * Cache Namespace Descriptions
 * Explanations of what each cache contains
 */
export const CACHE_NAMESPACE_DESCRIPTIONS: Record<CacheNamespace, string> = {
  menu: 'Restaurant menu data (categories, products, images)',
  'modifier-display': 'Product modifier display data per restaurant',
  'modifier-global': 'Shared modifier data across all restaurants',
  'vehicle-reference': 'Vehicle makes, models, and colors for curbside pickup',
  'eighty-sixed': 'Unavailable products and modifiers (2 min TTL)',
  restaurant: 'Restaurant information from Olo API (10 min TTL)',
  calendar: 'Store hours and calendar data from Olo API (15 min TTL)',
  'product-options': 'Product modifier/option data from Olo API (10 min TTL)',
  'audit-aggregate': 'Audit-log dashboard aggregate counts (cached query results)',
};

/**
 * Cache Namespace Color Classes
 * Tailwind classes for namespace badges
 */
export const CACHE_NAMESPACE_COLORS: Record<CacheNamespace, string> = {
  menu: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  'modifier-display': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  'modifier-global': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  'vehicle-reference': 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300',
  'eighty-sixed': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  restaurant: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  calendar: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
  'product-options': 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
  'audit-aggregate': 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-300',
};

/**
 * Redis Stats Interface
 * Server-level Redis statistics
 */
export interface RedisStats {
  /** Whether Redis is connected */
  connected: boolean;
  /** Human-readable memory usage (e.g., "1.5 GB") */
  memoryUsed: string;
  /** Peak memory usage (e.g., "2.0 GB") */
  memoryPeak: string;
  /** Total keys across all databases */
  totalKeys: number;
  /** Server uptime in seconds */
  uptime: number;
  /** Redis server version */
  version?: string;
}

/**
 * Cache Namespace Info Interface
 * Information about a specific cache namespace
 */
export interface CacheNamespaceInfo {
  /** Namespace identifier */
  name: CacheNamespace;
  /** Human-readable display name */
  displayName: string;
  /** Redis key pattern */
  pattern: string;
  /** Number of keys in this namespace */
  count: number;
  /** TTL in seconds (null for no expiration) */
  ttlSeconds: number | null;
  /** Description of what this cache contains */
  description: string;
}

/**
 * Cache Overview Response Interface
 * Complete overview of all caches
 */
export interface CacheOverviewResponse {
  /** Redis server statistics */
  redis: RedisStats;
  /** List of all cache namespaces with stats */
  namespaces: CacheNamespaceInfo[];
}

/**
 * Cache Entry Interface
 * A single cache entry with metadata
 */
export interface CacheEntry {
  /** Redis key */
  key: string;
  /** Whether the key exists */
  cached: boolean;
  /** Time to live in seconds (-2 if doesn't exist, -1 if no expiration) */
  ttlSeconds: number;
}

/**
 * Cache Lookup Response Interface
 * Result of looking up cache entries by ID
 */
export interface CacheLookupResponse {
  /** The namespace searched */
  namespace: CacheNamespace;
  /** The ID used for lookup */
  id: string;
  /** List of matching cache entries */
  entries: CacheEntry[];
}

/**
 * Invalidate Response Interface
 * Result of invalidating cache entries
 */
export interface InvalidateResponse {
  /** Success message */
  message: string;
  /** Number of keys deleted */
  keysDeleted: number;
}

/**
 * Bulk Invalidate Response Interface
 * Result of bulk invalidation operation
 */
export interface BulkInvalidateResponse {
  /** Results per namespace (partial since only requested namespaces are included) */
  results: Partial<Record<CacheNamespace, InvalidateResponse>>;
  /** Total keys deleted across all namespaces */
  totalKeysDeleted: number;
}

/**
 * Namespace Status Response Interface
 * Detailed status of a single namespace
 */
export interface NamespaceStatusResponse {
  /** Namespace identifier */
  namespace: CacheNamespace;
  /** Human-readable display name */
  displayName: string;
  /** Redis key pattern */
  pattern: string;
  /** Number of keys in this namespace */
  count: number;
  /** TTL in seconds */
  ttlSeconds: number | null;
  /** Description of what this cache contains */
  description: string;
}

/**
 * Invalidate Namespace Request Interface
 * Request body for invalidating a namespace
 */
export interface InvalidateNamespaceRequest {
  /** Optional ID to filter invalidation */
  id?: string;
}

/**
 * Bulk Invalidate Request Interface
 * Request body for bulk invalidation
 */
export interface BulkInvalidateRequest {
  /** List of namespaces to invalidate */
  namespaces: CacheNamespace[];
}

/**
 * Namespace Keys Response Interface
 * Result of listing all keys in a namespace
 */
export interface NamespaceKeysResponse {
  /** The namespace searched */
  namespace: CacheNamespace;
  /** List of cache entries */
  keys: CacheEntry[];
  /** Cursor for next page (null if no more pages) */
  nextCursor: string | null;
  /** Total count of keys in the namespace */
  totalCount: number;
  /** Whether there are more keys to fetch */
  hasMore: boolean;
}

/**
 * Delete Cache Key Response Interface
 * Result of deleting a specific cache key
 */
export interface DeleteCacheKeyResponse {
  /** Success/error message */
  message: string;
  /** Whether the key was deleted */
  deleted: boolean;
  /** The cache key that was deleted */
  key: string;
}

/**
 * Format uptime in human-readable format
 * @param seconds - Uptime in seconds
 * @returns Human-readable string (e.g., "5d 3h 20m")
 */
export function formatUptime(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }

  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);

  return parts.join(' ') || '0m';
}

/**
 * Format TTL in human-readable format
 * @param seconds - TTL in seconds
 * @returns Human-readable string (e.g., "1h 30m", "No expiration", "Not found")
 */
export function formatTTL(seconds: number | null): string {
  if (seconds === null) return 'No expiration';
  if (seconds === -2) return 'Not found';
  if (seconds === -1) return 'No expiration';
  if (seconds < 60) return `${seconds}s`;

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  return `${minutes}m`;
}

/**
 * Get status badge color based on cache entry state
 * @param entry - Cache entry
 * @returns Tailwind classes for status badge
 */
export function getCacheStatusColor(entry: CacheEntry): string {
  if (!entry.cached) {
    return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
  if (entry.ttlSeconds === -1) {
    return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
  }
  if (entry.ttlSeconds < 300) {
    return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
  }
  return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
}

/**
 * Get Redis connection status color
 * @param connected - Whether Redis is connected
 * @returns Tailwind classes for status indicator
 */
export function getRedisStatusColor(connected: boolean): string {
  return connected ? 'bg-green-500 dark:bg-green-400' : 'bg-red-500 dark:bg-red-400';
}
